import { useAtom } from '@reatom/npm-react';

import type { Context } from './context.ts';
import type { Observable } from './observable.ts';
import { useMappedObservable } from './useMappedObservable.ts';

export function useSubscribe<T>(o: Observable<T>): T;
export function useSubscribe<T, R>(o: Observable<T>, mapData: (data: T) => R, deps?: Array<unknown>): R;
export function useSubscribe<T>(map: (ctx: Context) => T): T;
export function useSubscribe<T, R>(o: Observable<T> | ((ctx: Context) => T), mapData?: (data: T) => R, deps?: Array<unknown>): T | R {
  const mapped = useMappedObservable((ctx) => {
    if (typeof o === 'function') {
      return o(ctx);
    }
    const value = ctx.spy(o);
    return mapData ? mapData(value) : value;
  }, deps);
  const [value] = useAtom(mapped.__INTERNAL_DO_NOT_USE_ATOM__, deps ?? []);

  return value;
}

export function useSubscribeCb<T>(map: (ctx: Context) => T, deps?: Array<unknown>): T {
  const mapped = useMappedObservable((ctx) => map(ctx), deps);
  const [value] = useAtom(mapped.__INTERNAL_DO_NOT_USE_ATOM__, deps ?? []);
  return value;
}

/**
 * @example const [foo, bar] =  useSubscribes([foo$, bar$])
 */
export function useSubscribeAll<T extends readonly Observable<unknown>[]>(
  observables: [...T],
): { [K in keyof T]: T[K] extends Observable<infer U> ? U : never } {
  const mapped = useMappedObservable((ctx) => observables.map((o) => ctx.spy(o)), []);
  const [values] = useAtom(mapped.__INTERNAL_DO_NOT_USE_ATOM__, []);
  return values as { [K in keyof T]: T[K] extends Observable<infer U> ? U : never };
}
